import React, { useEffect, useRef } from 'react';
import L from 'leaflet';
import { useMap } from 'react-leaflet';
import { createRoot } from 'react-dom/client'; // Import createRoot from client package
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';

/**
 * A MarkerClusterGroup component for React Leaflet that properly supports interactive popups
 */
const MarkerClusterGroup = (props) => {
  const map = useMap();
  const clusterGroupRef = useRef(null);
  const markersRef = useRef({});
  const popupRootsRef = useRef({});
  const reactRootsRef = useRef({});
  const featureGroupRef = useRef(null);
  
  // Create a heat map color based on cluster size
  const getHeatMapColor = (count) => {
    // Define colors for different ranges
    if (count < 10) return '#FFEB3B'; // Yellow for small clusters
    if (count < 20) return '#FFC107'; // Amber for medium-small clusters
    if (count < 50) return '#FF9800'; // Orange for medium clusters
    if (count < 100) return '#FF5722'; // Deep Orange for medium-large clusters
    return '#F44336'; // Red for large clusters
  };
  
  // Create custom cluster icons
  const createHeatMapCluster = (cluster) => {
    const count = cluster.getChildCount();
    const color = getHeatMapColor(count);
    
    return L.divIcon({
      html: `<div style="
        width: 36px; 
        height: 36px; 
        display: flex; 
        align-items: center; 
        justify-content: center; 
        background-color: ${color}; 
        color: white; 
        font-weight: bold; 
        border-radius: 50%; 
        border: 2px solid white; 
        box-shadow: 0 2px 5px rgba(0, 0, 0, 0.25);
      ">
        <span>${count}</span>
      </div>`,
      className: 'marker-cluster',
      iconSize: new L.Point(40, 40)
    });
  };

  // Initialize marker cluster group with optimized settings
  useEffect(() => {
    // Optimize cluster settings for better performance
    clusterGroupRef.current = L.markerClusterGroup({
      showCoverageOnHover: false, // Disable coverage hover for better performance
      zoomToBoundsOnClick: props.zoomToBoundsOnClick !== false, // Default true
      maxClusterRadius: props.maxClusterRadius || 80, // Increase radius for better clustering
      spiderfyOnMaxZoom: props.spiderfyOnMaxZoom !== false, // Default true
      disableClusteringAtZoom: props.disableClusteringAtZoom || 19, // Only uncluster at very high zoom
      iconCreateFunction: props.iconCreateFunction || createHeatMapCluster,
      chunkedLoading: true, // Always use chunked loading for better performance
      chunkInterval: 100, // Process in smaller chunks with a short delay between
      chunkDelay: 50, // Small delay between chunks for smoother loading
      singleMarkerMode: false,
      animate: true
    });
    
    featureGroupRef.current = L.featureGroup();
    
    // Add cluster group to map
    map.addLayer(clusterGroupRef.current);
    map.addLayer(featureGroupRef.current);
    
    // Clean up on unmount
    return () => {
      // Clean up React roots with modern API
      Object.keys(reactRootsRef.current).forEach(key => {
        if (reactRootsRef.current[key]) {
          const root = reactRootsRef.current[key];
          setTimeout(() => {
            try {
              root.unmount();
            } finally {
              if (reactRootsRef.current[key] === root) {
                delete reactRootsRef.current[key];
              }
              delete popupRootsRef.current[key];
            }
          }, 0);
        }
      });
      
      if (map) {
        if (clusterGroupRef.current) map.removeLayer(clusterGroupRef.current);
        if (featureGroupRef.current) map.removeLayer(featureGroupRef.current);
      }
      
      markersRef.current = {};
      popupRootsRef.current = {};
      reactRootsRef.current = {};
      clusterGroupRef.current = null;
      featureGroupRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, props.maxClusterRadius, props.spiderfyOnMaxZoom, props.showCoverageOnHover,
      props.zoomToBoundsOnClick, props.disableClusteringAtZoom, props.iconCreateFunction]);

  // Create specially formatted crime report content
  const formatCrimeReportContent = (report) => {
    if (report.type !== 'crime_report' || !report.description) return null;
    
    // Parse the crime report data
    const description = report.description;
    let parts = description.split('\n');
    
    // Extract title and population
    let title = parts[0] || 'Crime Report';
    let population = '';
    
    // Look for population information
    const popIndex = parts.findIndex(p => p.startsWith('Population:'));
    if (popIndex > -1) {
      population = parts[popIndex].replace('Population:', '').trim();
    }
    
    const container = document.createElement('div');
    container.className = 'crime-report-container';
    container.innerHTML = `
      <div style="font-family: system-ui, -apple-system, sans-serif; max-width: 300px;">
        <h3 style="margin-bottom: 5px; color: #2c3e50; font-size: 16px; border-bottom: 2px solid #3498db; padding-bottom: 8px;">
          ${title}
        </h3>
        
        ${population ? `
        <div style="display: flex; align-items: center; margin-bottom: 10px; background: #f8f9fa; padding: 6px; border-radius: 4px;">
          <span style="font-weight: bold; color: #2c3e50;">Population:</span>
          <span style="margin-left: 5px;">${population}</span>
        </div>` : ''}
        
        <div style="margin-top: 12px;">
          <table style="width: 100%; border-collapse: collapse; font-size: 13px;">
            <tbody>
              ${parts.slice(2).map(line => {
                // Skip empty lines
                if (!line.trim()) return '';
                
                // Check if it's a category header or data point
                if (line.includes(':')) {
                  const [key, value] = line.split(':').map(p => p.trim());
                  return `
                    <tr style="border-bottom: 1px solid #eee;">
                      <td style="padding: 4px 0; color: #34495e; font-weight: ${key.endsWith('Offenses') ? 'bold' : 'normal'};">${key}</td>
                      <td style="padding: 4px 0; text-align: right; font-weight: ${key.endsWith('Offenses') ? 'bold' : 'normal'};">${value}</td>
                    </tr>
                  `;
                } else {
                  return `
                    <tr style="background-color: #f8f9fa;">
                      <td colspan="2" style="padding: 4px; font-weight: bold; color: #2c3e50;">${line}</td>
                    </tr>
                  `;
                }
              }).join('')}
            </tbody>
          </table>
        </div>
        
        <div style="margin-top: 10px; font-size: 11px; color: #7f8c8d; text-align: right;">
         FBI NIBRS 2024 Data. Added: ${new Date(report.timestamp).toLocaleDateString()}
        </div>
      </div>
    `;
    return container;
  };

  // When children change, update markers
  useEffect(() => {
    // Skip if not initialized
    if (!clusterGroupRef.current || !featureGroupRef.current) return;
    
    const currentMarkerIds = new Set();
    
    // Process marker children
    React.Children.forEach(props.children, (child, index) => {
      if (!child || !child.props || !child.props.position) return;
      
      try {
        // Extract properties from child
        // Don't try to access key directly as it's a special React prop
        const { position, icon, report, eventHandlers, children } = child.props;
        // Use child.key which is how React exposes the key internally
        const markerId = child.key || `marker-${index}`;
        currentMarkerIds.add(markerId);
        
        // Create marker if it doesn't exist
        if (!markersRef.current[markerId]) {
          const marker = L.marker(position, { icon, report });
          
          // Handle special crime report formatting
          if (report && report.type === 'crime_report') {
            const crimeReportContent = formatCrimeReportContent(report);
            if (crimeReportContent) {
              marker.bindPopup(crimeReportContent, {
                maxWidth: 320,
                className: 'crime-report-popup'
              });
            }
          }
          // For normal reports and other marker types, render the React popup content
          else {
            // Create DOM node for popup content
            const popupRoot = document.createElement('div');
            popupRoot.className = 'popup-root';
            popupRootsRef.current[markerId] = popupRoot;
            
            // Create popup with this DOM node and fix width issues
            const popup = L.popup({
              maxWidth: 400,
              minWidth: 300,
              closeOnClick: false,
              autoClose: false,
              className: report?.type ? `report-type-${report.type}-popup` : ''
            }).setContent(popupRoot);
            
            marker.bindPopup(popup);

            marker.on('popupopen', () => {
              const popupElement = Array.isArray(children) ? children.find(Boolean) : children;
              const content = popupElement?.props?.children ?? popupElement;

              if (!reactRootsRef.current[markerId]) {
                reactRootsRef.current[markerId] = createRoot(popupRoot);
              }
              reactRootsRef.current[markerId].render(content);
            });

            marker.on('popupclose', () => {
              if (reactRootsRef.current[markerId]) {
                const root = reactRootsRef.current[markerId];
                setTimeout(() => {
                  try {
                    root.unmount();
                  } finally {
                    if (reactRootsRef.current[markerId] === root) {
                      delete reactRootsRef.current[markerId];
                    }
                  }
                }, 0);
              }
            });
          }
          
          // Add click handler if provided
          if (eventHandlers && eventHandlers.click) {
            marker.on('click', eventHandlers.click);
          }
          
          markersRef.current[markerId] = marker;
          
          // Add to cluster group
          if (report) {
            clusterGroupRef.current.addLayer(marker);
          } else {
            featureGroupRef.current.addLayer(marker);
          }
        } 
        // Update existing marker position
        else {
          const marker = markersRef.current[markerId];
          const currentLatLng = marker.getLatLng();
          const nextLat = Array.isArray(position) ? position[0] : position.lat;
          const nextLng = Array.isArray(position) ? position[1] : position.lng;
          const eps = 1e-7;

          if (
            !currentLatLng ||
            Math.abs(currentLatLng.lat - nextLat) > eps ||
            Math.abs(currentLatLng.lng - nextLng) > eps
          ) {
            marker.setLatLng(position);
          }
        }
      } catch (error) {
        console.warn('Error adding marker to cluster group:', error);
      }
    });
    
    // Clean up any markers that are no longer in the children
    Object.keys(markersRef.current).forEach(markerId => {
      if (!currentMarkerIds.has(markerId)) {
        const marker = markersRef.current[markerId];
        if (marker) {
          if (clusterGroupRef.current.hasLayer(marker)) {
            clusterGroupRef.current.removeLayer(marker);
          }
          if (featureGroupRef.current.hasLayer(marker)) {
            featureGroupRef.current.removeLayer(marker);
          }
          // Clean up popup React node if it exists - use setTimeout to avoid sync unmount during render
          if (reactRootsRef.current[markerId]) {
            const root = reactRootsRef.current[markerId];
            // Defer unmounting to avoid synchronous unmount during render
            setTimeout(() => {
              root.unmount();
              delete reactRootsRef.current[markerId];
              delete popupRootsRef.current[markerId];
            }, 0);
          }
          delete markersRef.current[markerId];
        }
      }
    });
  }, [props.children]);

  return null;
};

export default MarkerClusterGroup;
